import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Flex, Text, Tooltip } from "@radix-ui/themes";
import { useFeatureGate } from "@statsig/react-bindings";
import { useState } from "react";
import { LuChevronRight } from "react-icons/lu";
import { MdAdd, MdBolt } from "react-icons/md";
import { toast } from "react-toastify";

import AddPlaylistIcon from "@/assets/icons/add playlist.svg?react";
import MusicIcon from "@/assets/icons/Library audiolab music filled.svg?react";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import { useTrackUpload } from "@/components/providers/AudiolabProvider/hooks";
import NewReleaseChooser from "@/components/audiolab/NewReleaseChooser";
import { RELEASE_KIND_OPTIONS } from "@/components/audiolab/releaseKindOptions";
import { useCreateRelease } from "@/hooks/useCreateRelease";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { menuTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";

const itemClasses = cn(menuTokens.item, menuTokens.itemNeutral);

export type CreateMenuProps = {
  className?: string;
  /** Replaces the sidebar button, so other surfaces can offer the same menu. */
  trigger?: React.ReactNode;
  /**
   * The sidebar opens beside itself; elsewhere downward reads better, and the
   * mobile bottom bar has to open upward.
   */
  side?: "right" | "bottom" | "top";
};

/**
 * "Create" entry point for Audiolab: upload a track, start an album, or master
 * an existing track. Image and video uploads are left out until they exist —
 * a disabled option is still an option the user tries to click.
 */
export default function CreateMenu({
  className,
  trigger,
  side = "right",
}: CreateMenuProps) {
  const { setShowUpload, openUploadForMastering } = useTrackUpload();
  // "New release" skips the naming/add-tracks modals: it creates the release
  // and lands on its page, where title and tracks are edited in place.
  const { createRelease, isCreatingRelease, canCreate } = useCreateRelease();
  // Phones have no room for a flyout: the chooser asks Single or Album.
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [chooserOpen, setChooserOpen] = useState(false);

  const { data: user } = useLoggedInUser();

  // Uploads kick off the audiolab asset pipeline, which is centrally gated.
  const generationDisabled = useFeatureGate(
    "disable_audiolab_asset_generation",
  ).value;
  // The toolbar Upload button used to carry this check; folding it in here
  // means every entry point to the wizard honours it, desktop and mobile.
  const emailUnverified = user?.emailVerified === false;
  const uploadsDisabled = generationDisabled || emailUnverified;
  const uploadsBlockedReason = generationDisabled
    ? "Audiolab asset generation is temporarily disabled"
    : "Please verify your email to upload tracks";
  const uploadTooltip =
    emailUnverified && !generationDisabled
      ? "Verify your email address to upload tracks"
      : undefined;

  // z-60 clears the menu panel's z-50; rt-TooltipContent ships without one.
  const withUploadTooltip = (item: React.ReactElement) =>
    uploadTooltip ? (
      <Tooltip
        content={uploadTooltip}
        side="right"
        sideOffset={8}
        className="z-[60]"
      >
        {item}
      </Tooltip>
    ) : (
      item
    );

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        {trigger ?? (
          /* Modelled on Google Drive's New button: 56px tall, 16px radius and a
            Material elevation-2 shadow, sized to its content rather than the
            sidebar width. It rests at the page color and lifts to white on
            hover. A plain button because the themed Radix variants paint their
            own accent background and inset ring over all of this.

            The left inset and gap match NavButton so both the + and the word
            line up with the icons and labels underneath: 15px of padding (the
            1px border makes up NavButton's 16), a 22px icon column, and an 8px
            gap. Measured against the live sidebar — the two columns land on the
            same pixel. */
          <button
            type="button"
            data-testid="nav-create-menu"
            className={cn(
              "inline-flex h-[56px] w-fit cursor-pointer items-center gap-[8px]",
              // CSS `gray` (#808080) on purpose, not a palette token: the
              // design-system greys top out too light here — border-tertiary
              // is #E9EDF3 and border-secondary #D0D7E1, both of which
              // disappear against the white button.
              "rounded-[16px] border border-[#808080] bg-white pl-[15px] pr-[20px] text-text-primary",
              // Drive's own shadow is a three-layer Material stack, one layer
              // with negative spread. Against this page it read as a hard-edged
              // box behind the button, so this is a soft two-layer lift instead.
              "shadow-[0_1px_2px_rgba(16,24,40,0.06),0_6px_16px_-6px_rgba(16,24,40,0.16)]",
              "transition-[background-color,box-shadow] duration-150",
              "hover:bg-[var(--page-bg)] hover:shadow-[0_2px_4px_rgba(16,24,40,0.08),0_10px_22px_-8px_rgba(16,24,40,0.20)]",
              "outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
              className,
            )}
          >
            <MdAdd className="w-[22px] h-[22px] shrink-0" aria-hidden />
            <Text className="text-[14px] font-medium">Create</Text>
          </button>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        {/* Beside the sidebar rather than over it, so the nav stays readable
            while the menu is up. */}
        <DropdownMenu.Content
          side={side}
          align="start"
          sideOffset={10}
          collisionPadding={8}
          className={cn(menuTokens.panel, "min-w-[194px]")}
        >
          <Flex direction="column" gap="1">
            {withUploadTooltip(
              <DropdownMenu.Item
                className={itemClasses}
                disabled={uploadsDisabled}
                onSelect={() => {
                  if (uploadsDisabled) {
                    toast.info(uploadsBlockedReason);
                    return;
                  }
                  setShowUpload(true);
                }}
              >
                <MusicIcon width={16} height={16} className="fill-current" />
                <Text>Upload track</Text>
              </DropdownMenu.Item>,
            )}

            {withUploadTooltip(
              <DropdownMenu.Item
                className={itemClasses}
                disabled={uploadsDisabled}
                onSelect={() => {
                  if (uploadsDisabled) {
                    toast.info(uploadsBlockedReason);
                    return;
                  }
                  // Picking this already answers the wizard's mastering
                  // question, so it opens with that step settled.
                  openUploadForMastering();
                }}
              >
                <MdBolt className="text-[16px]" />
                <Text>Master track</Text>
              </DropdownMenu.Item>,
            )}

            <DropdownMenu.Separator className={menuTokens.separator} />

            {isMobile ? (
              <DropdownMenu.Item
                className={itemClasses}
                disabled={!canCreate || isCreatingRelease}
                onSelect={() => setChooserOpen(true)}
                data-testid="create-menu-new-release"
              >
                <AddPlaylistIcon
                  width={16}
                  height={16}
                  className="fill-current"
                />
                <Text>New release</Text>
              </DropdownMenu.Item>
            ) : (
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger
                  className={itemClasses}
                  disabled={!canCreate || isCreatingRelease}
                  data-testid="create-menu-new-release"
                >
                  <AddPlaylistIcon
                    width={16}
                    height={16}
                    className="fill-current"
                  />
                  <Text>
                    {isCreatingRelease ? "Creating..." : "New release"}
                  </Text>
                  <LuChevronRight className="ml-auto text-[14px] text-text-tertiary" />
                </DropdownMenu.SubTrigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.SubContent
                    sideOffset={8}
                    collisionPadding={8}
                    className={cn(menuTokens.panel, "min-w-[160px]")}
                  >
                    {RELEASE_KIND_OPTIONS.map(({ kind, title, Icon }) => (
                      <DropdownMenu.Item
                        key={kind}
                        className={itemClasses}
                        onSelect={() => createRelease(kind)}
                        data-testid={`create-menu-new-${kind.toLowerCase()}`}
                      >
                        <Icon size={16} />
                        <Text>{title}</Text>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.SubContent>
                </DropdownMenu.Portal>
              </DropdownMenu.Sub>
            )}
          </Flex>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>

      <NewReleaseChooser open={chooserOpen} onOpenChange={setChooserOpen} />
    </DropdownMenu.Root>
  );
}
